/**
 * AvatarSelector — UI do wyboru / tworzenia / edycji avatara (persony).
 * Localstorage-backed via useAvatars hook. Persistencja DB w Sprint 3.
 */
import { useRef, useState } from "react";
import { useAvatars, type Avatar } from "@/hooks/useAvatars";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  User,
  Plus,
  Pencil,
  Trash2,
  Check,
  Upload,
  Loader2,
  ImageIcon,
  Copy,
  Download,
  Sparkles,
  Link2,
} from "lucide-react";
import { AVATAR_TEMPLATES, type AvatarTemplate } from "./avatar-templates";
import { toast } from "@/hooks/use-toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";

const friendlyErrorToast = (err: unknown, fallbackTitle: string): void => {
  const friendly = FriendlyErrorMapper.map(err);
  toast({
    variant: "destructive",
    title: fallbackTitle,
    description: friendly.description,
  });
};

interface AvatarSelectorProps {
  onContextChange?: (contextText: string) => void;
}

export default function AvatarSelector({ onContextChange }: AvatarSelectorProps) {
  const {
    avatars,
    activeId,
    activeAvatar,
    createAvatar,
    updateAvatar,
    deleteAvatar,
    setActiveAvatar,
    uploadPhoto,
    isUploadingPhoto,
    duplicateAvatar,
    isDuplicating,
    shareAvatar,
    isSharing,
  } = useAvatars();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const [editingAvatar, setEditingAvatar] = useState<Avatar | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftContext, setDraftContext] = useState("");
  const [draftVoiceRefs, setDraftVoiceRefs] = useState("");

  const openCreate = () => {
    setIsCreating(true);
    setEditingAvatar(null);
    setDraftName("");
    setDraftContext("");
    setDraftVoiceRefs("");
  };

  const openEdit = (avatar: Avatar) => {
    setEditingAvatar(avatar);
    setIsCreating(false);
    setDraftName(avatar.name);
    setDraftContext(avatar.contextText);
    setDraftVoiceRefs(avatar.voiceRefs.join("\n"));
  };

  const closeDialog = () => {
    setEditingAvatar(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    const name = draftName.trim();
    if (!name) return;
    const voiceRefs = draftVoiceRefs
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      if (editingAvatar) {
        await updateAvatar(editingAvatar.id, {
          name,
          contextText: draftContext,
          voiceRefs,
        });
      } else {
        const created = await createAvatar({
          name,
          contextText: draftContext,
          voiceRefs,
          usePhotoByDefault: false,
        });
        setActiveAvatar(created.id);
        onContextChange?.(created.contextText);
      }
      closeDialog();
    } catch {
      // Mutation already surfaces the error toast via global handler
    }
  };

  const handlePick = (avatar: Avatar) => {
    setActiveAvatar(avatar.id);
    onContextChange?.(avatar.contextText);
  };

  const dialogOpen = isCreating || editingAvatar !== null;

  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] text-white/50 flex items-center gap-1">
        <User className="w-3 h-3 text-red-400" /> Avatar
        {activeAvatar && (
          <span className="text-[9px] text-emerald-400/70">({activeAvatar.name})</span>
        )}
      </Label>
      <div className="flex gap-1.5 flex-wrap">
        {avatars.map((avatar) => {
          const isActive = avatar.id === activeId;
          return (
            <div
              key={avatar.id}
              className={`group relative flex items-center gap-1 rounded-lg border transition-all ${
                isActive
                  ? "bg-emerald-600/15 border-emerald-500/50 text-emerald-200"
                  : "bg-white/[0.03] border-white/[0.08] text-white/60 hover:text-white hover:border-white/[0.15]"
              }`}
            >
              <button
                onClick={() => handlePick(avatar)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium"
                title={avatar.contextText.slice(0, 200) || "Brak kontekstu"}
              >
                {isActive && <Check className="w-3 h-3" />}
                {avatar.name}
              </button>
              <button
                onClick={() => openEdit(avatar)}
                className="px-1 py-1.5 opacity-50 hover:opacity-100 transition-opacity"
                title="Edytuj"
              >
                <Pencil className="w-3 h-3" />
              </button>
            </div>
          );
        })}
        <div className="relative group">
          <button
            onClick={openCreate}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-dashed border-white/[0.15] text-[11px] text-white/50 hover:text-white hover:border-white/[0.3] transition-all"
          >
            <Plus className="w-3 h-3" /> {avatars.length === 0 ? "Stwórz avatar" : "Nowy"}
          </button>
          {avatars.length > 0 && (
            <div className="absolute left-0 top-full mt-1 z-20 hidden group-hover:block hover:block min-w-[200px] rounded-xl border border-white/[0.1] bg-[#0a0c10] py-1 shadow-2xl shadow-black/70">
              <div className="px-3 py-1 text-[9px] uppercase text-white/40 font-semibold flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5 text-amber-400/70" /> Z szablonu:
              </div>
              {AVATAR_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  onClick={async () => {
                    try {
                      const created = await createAvatar({
                        name: tpl.name,
                        contextText: tpl.contextText,
                        voiceRefs: tpl.voiceRefs,
                        usePhotoByDefault: false,
                      });
                      setActiveAvatar(created.id);
                    } catch {
                      /* mutation toast */
                    }
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-left text-white/65 hover:bg-white/[0.04] hover:text-white transition-colors"
                >
                  <span>{tpl.emoji}</span>
                  <span className="truncate">{tpl.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          ref={importInputRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            e.target.value = "";
            try {
              const text = await file.text();
              const parsed = JSON.parse(text) as Record<string, unknown>;
              // Collection bulk import (Sprint 28)
              if (parsed && parsed.type === "avatar-collection" && Array.isArray(parsed.avatars)) {
                let imported = 0;
                for (const item of parsed.avatars) {
                  const a = item as Partial<{
                    name: string;
                    contextText: string;
                    voiceRefs: string[];
                    usePhotoByDefault: boolean;
                    photoUrl: string | null;
                  }>;
                  if (typeof a.name !== "string" || !a.name.trim()) continue;
                  await createAvatar({
                    name: a.name.slice(0, 80),
                    contextText: typeof a.contextText === "string" ? a.contextText : "",
                    voiceRefs: Array.isArray(a.voiceRefs)
                      ? a.voiceRefs.filter((v): v is string => typeof v === "string")
                      : [],
                    usePhotoByDefault:
                      typeof a.usePhotoByDefault === "boolean" ? a.usePhotoByDefault : false,
                    photoUrl: typeof a.photoUrl === "string" ? a.photoUrl : null,
                  });
                  imported++;
                }
                toast({
                  title: `Zaimportowano ${imported} avatarów`,
                  description: "Wszystko gotowe — wybierz aktywny avatar z listy.",
                });
                return;
              }
              // Single avatar legacy format
              const single = parsed as Partial<{
                name: string;
                contextText: string;
                voiceRefs: string[];
                usePhotoByDefault: boolean;
                photoUrl: string | null;
              }>;
              if (typeof single.name !== "string" || !single.name.trim()) {
                toast({
                  variant: "destructive",
                  title: "Plik avatara wygląda na uszkodzony",
                  description:
                    'Brakuje pola "name". Sprawdź czy plik JSON pochodzi z eksportu z viral.partners.',
                });
                return;
              }
              const created = await createAvatar({
                name: single.name.slice(0, 80),
                contextText: typeof single.contextText === "string" ? single.contextText : "",
                voiceRefs: Array.isArray(single.voiceRefs)
                  ? single.voiceRefs.filter((v): v is string => typeof v === "string")
                  : [],
                usePhotoByDefault:
                  typeof single.usePhotoByDefault === "boolean" ? single.usePhotoByDefault : false,
                photoUrl: typeof single.photoUrl === "string" ? single.photoUrl : null,
              });
              setActiveAvatar(created.id);
            } catch (err) {
              friendlyErrorToast(err, "Nie udało się zaimportować avatara");
            }
          }}
        />
        <button
          onClick={() => importInputRef.current?.click()}
          title="Importuj avatar z pliku JSON"
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-dashed border-white/[0.1] text-[11px] text-white/40 hover:text-white/70 hover:border-white/[0.2] transition-all"
        >
          <Upload className="w-3 h-3" />
        </button>
        <button
          onClick={async () => {
            const url = window.prompt("Wklej link do udostępnionego avatara:");
            if (!url || !url.trim()) return;
            try {
              const res = await fetch(url.trim());
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const parsed = (await res.json()) as Partial<{
                name: string;
                contextText: string;
                voiceRefs: string[];
                usePhotoByDefault: boolean;
                photoUrl: string | null;
              }>;
              if (typeof parsed.name !== "string" || !parsed.name.trim()) {
                toast({
                  variant: "destructive",
                  title: "Ten link nie wygląda na avatar",
                  description:
                    'Pobrane dane nie mają pola "name". Sprawdź czy URL pochodzi z viral.partners.',
                });
                return;
              }
              const created = await createAvatar({
                name: parsed.name.slice(0, 80),
                contextText: typeof parsed.contextText === "string" ? parsed.contextText : "",
                voiceRefs: Array.isArray(parsed.voiceRefs)
                  ? parsed.voiceRefs.filter((v): v is string => typeof v === "string")
                  : [],
                usePhotoByDefault:
                  typeof parsed.usePhotoByDefault === "boolean" ? parsed.usePhotoByDefault : false,
                photoUrl: typeof parsed.photoUrl === "string" ? parsed.photoUrl : null,
              });
              setActiveAvatar(created.id);
            } catch (err) {
              friendlyErrorToast(err, "Nie udało się pobrać avatara z linku");
            }
          }}
          title="Importuj avatar z linku URL"
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-dashed border-white/[0.1] text-[11px] text-white/40 hover:text-white/70 hover:border-white/[0.2] transition-all"
        >
          <Link2 className="w-3 h-3" />
        </button>
        {avatars.length > 0 && (
          <button
            onClick={async () => {
              if (
                !window.confirm(
                  `Usunąć wszystkie ${avatars.length} avatary? Tej akcji nie można cofnąć.`
                )
              )
                return;
              for (const a of avatars) {
                await deleteAvatar(a.id).catch(() => undefined);
              }
            }}
            title="Usuń wszystkie avatary z biblioteki"
            className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-dashed border-red-500/30 text-[11px] text-red-300/60 hover:text-red-300 hover:border-red-500/60 transition-all"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
        {avatars.length > 1 && (
          <button
            onClick={() => {
              const exportData = {
                version: 1,
                type: "avatar-collection",
                exportedAt: new Date().toISOString(),
                count: avatars.length,
                avatars: avatars.map((a) => ({
                  name: a.name,
                  contextText: a.contextText,
                  voiceRefs: a.voiceRefs,
                  usePhotoByDefault: a.usePhotoByDefault,
                  photoUrl: a.photoUrl,
                })),
              };
              const blob = new Blob([JSON.stringify(exportData, null, 2)], {
                type: "application/json",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `avatars-backup-${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            title="Eksportuj wszystkie avatary jako backup"
            className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-dashed border-white/[0.1] text-[11px] text-white/40 hover:text-white/70 hover:border-white/[0.2] transition-all"
          >
            <Download className="w-3 h-3" />
            <span className="text-[9px]">{avatars.length}</span>
          </button>
        )}
      </div>
      {avatars.length === 0 && (
        <div className="space-y-2">
          <p className="text-[10px] text-white/40 leading-relaxed">
            Avatar = nazwana persona (kontekst, jak się komunikujesz, voice refs). Jeden klik
            podpina go do generatora.
          </p>
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-amber-400/70" />
            <p className="text-[10px] text-white/55 font-medium">Szybki start — wybierz szablon:</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {AVATAR_TEMPLATES.map((tpl: AvatarTemplate) => (
              <button
                key={tpl.id}
                onClick={async () => {
                  try {
                    const created = await createAvatar({
                      name: tpl.name,
                      contextText: tpl.contextText,
                      voiceRefs: tpl.voiceRefs,
                      usePhotoByDefault: false,
                    });
                    setActiveAvatar(created.id);
                    onContextChange?.(created.contextText);
                  } catch {
                    /* mutation toast */
                  }
                }}
                className="text-left p-2 rounded-lg border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.15] transition-all"
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-base leading-none">{tpl.emoji}</span>
                  <span className="text-[11px] font-medium text-white truncate">{tpl.name}</span>
                </div>
                <p className="text-[9px] text-white/45 leading-snug line-clamp-2">
                  {tpl.shortDescription}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="bg-[#0a0c10] border-white/[0.1] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAvatar ? "Edytuj avatar" : "Nowy avatar"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-white/60 mb-1">Nazwa</Label>
              <Input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="np. Mateusz, Pamela, Brand VP..."
                className="bg-[#030407] border-white/[0.1] text-white"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-white/60">
                  Kontekst (kim jestem, jak się komunikuję)
                </Label>
                {(() => {
                  const len = draftContext.length;
                  const isLow = len > 0 && len < 100;
                  const isGood = len >= 200 && len <= 1500;
                  const isHigh = len > 2000;
                  const cls = isLow
                    ? "text-amber-400/70"
                    : isGood
                      ? "text-emerald-400/70"
                      : isHigh
                        ? "text-red-400/70"
                        : "text-white/30";
                  return (
                    <span className={`text-[9px] font-mono ${cls}`}>
                      {len} znaków{isLow && " · za mało"}
                      {isGood && " · ok"}
                      {isHigh && " · max ~2000"}
                    </span>
                  );
                })()}
              </div>
              <Textarea
                value={draftContext}
                onChange={(e) => setDraftContext(e.target.value)}
                placeholder="Jestem... Komunikuję się... Używam słów: ... Nie używam: ..."
                rows={6}
                className="bg-[#030407] border-white/[0.1] text-white text-xs"
              />
              <p className="text-[9px] text-white/30 mt-1">
                Dłuższy kontekst (200-1500 znaków) = lepsze trafianie w Twój styl.
              </p>
            </div>
            {editingAvatar && (
              <div>
                <Label className="text-xs text-white/60 mb-1">Zdjęcie avatara (opcjonalne)</Label>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !editingAvatar) return;
                    e.target.value = "";
                    try {
                      const updated = await uploadPhoto(editingAvatar.id, file);
                      setEditingAvatar(updated);
                    } catch {
                      // toast already shown by mutation
                    }
                  }}
                />
                <div className="flex items-center gap-2">
                  {editingAvatar.photoUrl ? (
                    <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/[0.08]">
                      <img
                        src={editingAvatar.photoUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg border border-dashed border-white/[0.15] flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-white/30" />
                    </div>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={isUploadingPhoto}
                    className="border-white/[0.1] text-white/70"
                  >
                    {isUploadingPhoto ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5 mr-1" />
                    )}
                    {editingAvatar.photoUrl ? "Zmień zdjęcie" : "Wgraj zdjęcie"}
                  </Button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="usePhotoByDefault"
                    type="checkbox"
                    checked={editingAvatar.usePhotoByDefault}
                    onChange={async (e) => {
                      await updateAvatar(editingAvatar.id, { usePhotoByDefault: e.target.checked });
                      setEditingAvatar((prev) =>
                        prev ? { ...prev, usePhotoByDefault: e.target.checked } : prev
                      );
                    }}
                    className="w-3.5 h-3.5 accent-[#ef3e29]"
                  />
                  <label htmlFor="usePhotoByDefault" className="text-[10px] text-white/60">
                    Domyślnie używaj zdjęcia w grafikach
                  </label>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs text-white/60 mb-1">
                Voice refs (linki YouTube, jeden na linię)
              </Label>
              <Textarea
                value={draftVoiceRefs}
                onChange={(e) => setDraftVoiceRefs(e.target.value)}
                placeholder="https://youtube.com/watch?v=...&#10;https://youtube.com/watch?v=..."
                rows={3}
                className="bg-[#030407] border-white/[0.1] text-white text-xs font-mono"
              />
              {(() => {
                const refs = draftVoiceRefs
                  .split("\n")
                  .map((s) => s.trim())
                  .filter(Boolean);
                const ytIdRe =
                  /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,15})/;
                const nonYt = refs.filter((r) => !ytIdRe.test(r));
                if (nonYt.length === 0) return null;
                return (
                  <p className="text-[10px] text-amber-300/80 mt-1">
                    ⚠ {nonYt.length}/{refs.length}{" "}
                    {nonYt.length === 1 ? "link nie jest" : "linki nie są"} YouTube — AI może nie
                    pobrać transcriptu.
                  </p>
                );
              })()}
              <p className="text-[10px] text-white/30 mt-1">
                AI nauczy się stylu wypowiedzi z tych referencji (transcripty + retrieval)
              </p>
              {/* Visual preview of YouTube thumbnails (Sprint 33) */}
              {(() => {
                const refs = draftVoiceRefs
                  .split("\n")
                  .map((s) => s.trim())
                  .filter(Boolean);
                const ytIdFor = (url: string): string | null => {
                  const m = url.match(
                    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,15})/
                  );
                  return m ? m[1] : null;
                };
                const previews = refs
                  .map((r, i) => ({ url: r, id: ytIdFor(r), index: i }))
                  .filter((p): p is { url: string; id: string; index: number } => p.id !== null);
                if (previews.length === 0) return null;
                return (
                  <div className="grid grid-cols-3 gap-1.5 mt-2">
                    {previews.map((p) => (
                      <a
                        key={`${p.id}-${p.index}`}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative block rounded-md overflow-hidden border border-white/[0.08] hover:border-white/[0.2] transition-colors"
                      >
                        <img
                          src={`https://img.youtube.com/vi/${p.id}/mqdefault.jpg`}
                          alt={`YT ${p.id}`}
                          className="w-full h-auto block"
                          loading="lazy"
                        />
                        <span className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors" />
                      </a>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            {editingAvatar && (
              <>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const dup = await duplicateAvatar(editingAvatar.id);
                      setActiveAvatar(dup.id);
                      closeDialog();
                    } catch {
                      /* mutation toast */
                    }
                  }}
                  disabled={isDuplicating}
                  className="border-white/[0.1] text-white/70 mr-auto"
                >
                  {isDuplicating ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Copy className="w-3.5 h-3.5 mr-1" />
                  )}
                  Duplikuj
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const url = await shareAvatar(editingAvatar.id);
                      const fullUrl = url.startsWith("http")
                        ? url
                        : `${window.location.origin}${url}`;
                      await navigator.clipboard.writeText(fullUrl);
                      toast({
                        title: "Link skopiowany do schowka",
                        description: "Możesz teraz wysłać go komu chcesz — wystarczy wkleić.",
                      });
                    } catch (err) {
                      friendlyErrorToast(err, "Nie udało się utworzyć linku");
                    }
                  }}
                  disabled={isSharing}
                  className="border-white/[0.1] text-white/70"
                >
                  {isSharing ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Link2 className="w-3.5 h-3.5 mr-1" />
                  )}
                  Udostępnij link
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const exportData = {
                      name: editingAvatar.name,
                      contextText: editingAvatar.contextText,
                      voiceRefs: editingAvatar.voiceRefs,
                      usePhotoByDefault: editingAvatar.usePhotoByDefault,
                      exportedAt: new Date().toISOString(),
                    };
                    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
                      type: "application/json",
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `avatar-${editingAvatar.name.replace(/[^a-z0-9]/gi, "_")}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="border-white/[0.1] text-white/70"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Eksportuj
                </Button>
              </>
            )}
            {editingAvatar && (
              <Button
                variant="outline"
                onClick={async () => {
                  if (confirm(`Usunąć avatar "${editingAvatar.name}"?`)) {
                    await deleteAvatar(editingAvatar.id);
                    closeDialog();
                  }
                }}
                className="border-red-500/30 text-red-300 hover:bg-red-600/10"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Usuń
              </Button>
            )}
            <Button
              variant="outline"
              onClick={closeDialog}
              className="border-white/[0.1] text-white/70"
            >
              Anuluj
            </Button>
            <Button
              onClick={handleSave}
              disabled={!draftName.trim()}
              className="bg-[#ef3e29] hover:bg-[#d63520]"
            >
              {editingAvatar ? "Zapisz zmiany" : "Stwórz"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
